import { LevelType, ProjectAttachmentType } from 'data/types';
import type { StructureImageAssignmentType } from 'features/structure/structure-slice';

type StructureImageAssignments = Record<
  number,
  StructureImageAssignmentType | null
>;

export type InheritedLayoutImageTransform = {
  scale: number;
  rotation: number;
  startPositionX: number;
  startPositionY: number;
};

type ResolveLevelLayoutAttachmentIdInput = {
  structure: LevelType[];
  activeLevelIndex: number;
  structureImageAssignments: StructureImageAssignments;
};

function findAssignedLayoutAttachmentIdForLevel(
  levelUniqueId: string,
  structureImageAssignments: StructureImageAssignments
): number | null {
  const assignedEntry = Object.entries(structureImageAssignments).find(
    ([, assignment]) =>
      assignment?.structureItemUniqueId === levelUniqueId &&
      assignment?.structureItemTypeCode === 'level'
  );

  if (!assignedEntry) {
    return null;
  }

  return Number(assignedEntry[0]);
}

export function levelHasAssignedLayoutImage(
  levelUniqueId: string | undefined,
  structureImageAssignments: StructureImageAssignments,
  structureAttachments: ProjectAttachmentType[]
): boolean {
  if (!levelUniqueId) {
    return false;
  }

  const attachmentId = findAssignedLayoutAttachmentIdForLevel(
    levelUniqueId,
    structureImageAssignments
  );

  if (attachmentId === null) {
    return false;
  }

  return structureAttachments.some(
    (attachment) => attachment.id === attachmentId
  );
}

export function resolveLevelLayoutAttachmentId({
  structure,
  activeLevelIndex,
  structureImageAssignments
}: ResolveLevelLayoutAttachmentIdInput): number | null {
  for (let i = activeLevelIndex; i >= 0; i -= 1) {
    const level = structure[i];
    if (level) {
      const attachmentId = findAssignedLayoutAttachmentIdForLevel(
        level.uniqueId,
        structureImageAssignments
      );
      if (attachmentId !== null) return attachmentId;
    }
  }

  for (let i = activeLevelIndex + 1; i < structure.length; i += 1) {
    const level = structure[i];
    if (level) {
      const attachmentId = findAssignedLayoutAttachmentIdForLevel(
        level.uniqueId,
        structureImageAssignments
      );
      if (attachmentId !== null) return attachmentId;
    }
  }

  return null;
}
